import React from 'react';
import {
  xpToLevel,
  xpWithinLevel,
  xpRangeForLevel,
  xpCeilForLevel,
  COSMETICS_CONFIG,
} from '../gamificationConfig';

export default function GlobalPlayerCard({ playerData, themeBProgress, themeAProgress = null, showThemeA = false }) {
  // The avatar is the whole student: its level is driven by the combined pool
  // (both themes summed), falling back to per-language XP for older payloads.
  const avatarXP   = playerData.combinedXP ?? playerData.globalXP ?? playerData.lifetimeXP;
  const level      = xpToLevel(avatarXP);
  const xpInLevel  = xpWithinLevel(avatarXP);
  const xpRange    = xpRangeForLevel(level);   // span of the current level (varies)
  const xpCeil     = xpCeilForLevel(level);    // absolute XP needed for next level
  const xpBarPct   = (xpInLevel / xpRange) * 100;

  const equippedRing   = COSMETICS_CONFIG.rings.find(r => r.id === playerData.equippedRingId)
    ?? COSMETICS_CONFIG.rings[1];
  const equippedAvatar = COSMETICS_CONFIG.avatars.find(a => a.id === playerData.equippedAvatarId)
    ?? COSMETICS_CONFIG.avatars[0];
  const equippedPet    = COSMETICS_CONFIG.pets.find(p => p.id === playerData.equippedPetId)
    ?? null;
  const showPet        = equippedPet && equippedPet.id !== 'pet_none';

  const ringStyle = equippedRing.cssColor
    ? { boxShadow: `0 0 0 3px var(--bg), 0 0 0 6px ${equippedRing.cssColor}` }
    : {};

  return (
    <div className="gpc-card">

      {/* Avatar + ring + pet */}
      <div className="gpc-avatar-wrap">
        <div className="gpc-avatar" style={ringStyle}>
          <span role="img" aria-label={equippedAvatar.label}>{equippedAvatar.emoji}</span>
        </div>
        {showPet && (
          <span className="gpc-pet" role="img" aria-label={equippedPet.label} title={equippedPet.label}>
            {equippedPet.emoji}
          </span>
        )}
      </div>

      {/* Identity */}
      <div className="gpc-identity">
        <div className="gpc-level-badge">Level {level}</div>
        <div className="gpc-name">{playerData.name}</div>
      </div>

      <div className="gpc-divider" aria-hidden="true" />

      {/* Per-theme mastery — distinct readouts, one shared avatar level */}
      <div className="gpc-metric-col">
        <div className="gpc-metric-label">Theme B Progress</div>
        <div className="gpc-metric-value ib-mono">{Math.round(themeBProgress)}%</div>
        <div className="gpc-bar-track">
          <div className="gpc-bar-fill gpc-bar-theme" style={{ width: `${Math.min(themeBProgress, 100)}%` }} />
        </div>
      </div>

      {showThemeA && (
        <>
          <div className="gpc-divider" aria-hidden="true" />

          <div className="gpc-metric-col">
            <div className="gpc-metric-label">Theme A Mastery</div>
            <div className="gpc-metric-value ib-mono">
              {themeAProgress === null ? '—' : `${Math.round(themeAProgress)}%`}
            </div>
            <div className="gpc-bar-track">
              <div className="gpc-bar-fill gpc-bar-theme-a" style={{ width: `${Math.min(themeAProgress ?? 0, 100)}%` }} />
            </div>
          </div>
        </>
      )}

      <div className="gpc-divider" aria-hidden="true" />

      {/* XP to next level */}
      <div className="gpc-metric-col gpc-metric-col-wide">
        <div className="gpc-xp-header">
          <span className="gpc-metric-label">XP to Level {level + 1}</span>
          <span className="gpc-xp-count ib-mono">{xpInLevel.toLocaleString()} / {xpRange.toLocaleString()}</span>
        </div>
        <div className="gpc-bar-track">
          <div className="gpc-bar-fill gpc-bar-xp" style={{ width: `${Math.min(xpBarPct, 100)}%` }} />
        </div>
        <div className="gpc-lifetime-xp ib-mono">{avatarXP.toLocaleString()} XP · next level at {xpCeil.toLocaleString()}</div>
      </div>

    </div>
  );
}
